import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Eye,
  Waves,
  MapPin,
  Battery,
  ArrowRight,
  Shield,
  Zap,
  Settings,
  Volume2
} from 'lucide-react';

const FeaturesPage = () => {
  const mainFeatures = [
    {
      icon: <Eye className="w-8 h-8" />,
      title: 'Forward Obstacle Detection',
      subtitle: 'See what\'s ahead',
      description: 'Advanced sensors continuously scan the path in front of you, detecting obstacles such as walls, furniture, people, and other objects before you reach them.',
      details: [
        'Real-time scanning of the path ahead',
        'Gentle alerts warn you before contact',
        'Adjusts to various environments'
      ],
      color: 'blue'
    },
    {
      icon: <Waves className="w-8 h-8" />,
      title: 'Surface Detection',
      subtitle: 'Know what\'s below',
      description: 'Downward-facing sensors analyze the ground surface continuously, identifying hazards like potholes, steps, curbs, drops, and changes in terrain.',
      details: [
        'Detects potholes and uneven ground',
        'Warns of steps, curbs, and drops',
        'Identifies changes in surface type'
      ],
      color: 'purple'
    },
    {
      icon: <MapPin className="w-8 h-8" />,
      title: 'Apple Find My Integration',
      subtitle: 'Never lose your way',
      description: 'Built-in Apple Find My support means you can always locate your ClearWay stick. If misplaced, simply use the Find My app on any Apple device.',
      details: [
        'Works with iPhone, iPad, and Mac',
        'Precision Finding for exact location',
        'End-to-end encrypted location data'
      ],
      color: 'green'
    },
    {
      icon: <Battery className="w-8 h-8" />,
      title: 'Reliable Power Design',
      subtitle: 'Always ready',
      description: 'ClearWay is designed for simplicity and dependability. No rechargeable batteries to manage — the device is ready when you need it.',
      details: [
        'No charging routines to remember',
        'Designed for long-term reliability',
        'Simple battery replacement'
      ],
      color: 'orange'
    }
  ];

  const additionalFeatures = [
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'Proactive Safety',
      description: 'Alerts come before you encounter hazards, giving you time to react.'
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: 'Instant Feedback',
      description: 'Intuitive haptic or audio feedback that guides without overwhelming.'
    },
    {
      icon: <Settings className="w-6 h-6" />,
      title: 'Complete Integration',
      description: 'All technology is built into one elegant device — not an add-on accessory.'
    },
    {
      icon: <Volume2 className="w-6 h-6" />,
      title: 'Intuitive Alerts',
      description: 'Clear, easy-to-understand feedback that becomes second nature.'
    }
  ];

  const colorClasses = {
    blue: 'from-blue-500/20 to-blue-500/0 border-blue-500/20',
    purple: 'from-purple-500/20 to-purple-500/0 border-purple-500/20',
    green: 'from-green-500/20 to-green-500/0 border-green-500/20',
    orange: 'from-orange-500/20 to-orange-500/0 border-orange-500/20'
  };

  const iconColorClasses = {
    blue: 'bg-blue-500/10 text-blue-400',
    purple: 'bg-purple-500/10 text-purple-400',
    green: 'bg-green-500/10 text-green-400',
    orange: 'bg-orange-500/10 text-orange-400'
  };

  return (
    <div className="pt-24">
      {/* Hero */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-blue-400 text-sm font-medium tracking-widest uppercase mb-4">
              Features
            </p>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6">
              Designed for
              <br />
              <span className="text-gray-500">Real Life</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Every feature in ClearWay exists to solve real problems faced by 
              visually impaired and elderly users every day.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Features */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="space-y-8">
            {mainFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`relative rounded-3xl p-8 md:p-12 bg-gradient-to-br ${colorClasses[feature.color]} border overflow-hidden`}
              >
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  <div>
                    <div className={`w-16 h-16 rounded-2xl ${iconColorClasses[feature.color]} flex items-center justify-center mb-6`}>
                      {feature.icon}
                    </div>
                    <p className="text-sm text-gray-400 uppercase tracking-wider mb-2">
                      {feature.subtitle}
                    </p>
                    <h2 className="text-3xl md:text-4xl font-semibold mb-4">
                      {feature.title}
                    </h2>
                    <p className="text-gray-400 text-lg leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                  <div className="space-y-4">
                    {feature.details.map((detail, i) => (
                      <div key={i} className="flex items-center gap-4 p-4 rounded-xl bg-white/5">
                        <div className="w-2 h-2 rounded-full bg-white/50" />
                        <span className="text-gray-300">{detail}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Features Grid */}
      <section className="py-24 px-6 bg-[#0f0f0f]">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-semibold mb-4">Thoughtful Details</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Beyond the core features, every aspect of ClearWay is designed with the user in mind.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {additionalFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="p-6 rounded-2xl bg-gray-800/30 border border-gray-800/50 hover:border-gray-700 transition-colors"
              >
                <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center text-gray-300 mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-medium mb-2">{feature.title}</h3>
                <p className="text-gray-500 text-sm">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-semibold mb-6">
              See the technology behind ClearWay
            </h2>
            <p className="text-gray-400 mb-8">
              Learn how our sensing system works to keep you safe.
            </p>
            <Link
              to="/technology"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-white text-black font-medium hover:bg-gray-100 transition-all"
            >
              Explore Technology
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default FeaturesPage;
